// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION root.
//
// Sets up react-router-dom. The default route is the Dashboard, the first
// migrated screen and the visual proof of the new stack. Contacts is the second
// migrated screen (/contacts). As more screens are ported (Schedule, Releases,
// …) they get added as routes here, and the shared TopNav's tabs navigate
// between them via react-router (the active tab is derived from the route).
// ─────────────────────────────────────────────────────────────────────────────

import { Suspense, useState, useEffect } from 'react';
import { lazyScreen, ScreenErrorBoundary } from './lib/lazyScreen';
import type { JSX } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isAuthenticated, finalizeOAuthReturn, takeOAuthReturnError, claimPendingShopifyExchange, getCachedUser, fetchCurrentUser } from './lib';
import type { User } from './lib';
import { DesktopLogin } from './screens/DesktopLogin';
import { Dashboard } from './screens/Dashboard';
/* code-split 2026-07-14 (perf tranche, Dev consult): every screen except the
   login + the Dashboard landing loads as its OWN chunk on first navigation.
   The main bundle had crossed Vite's 500kB warning with all screens eager;
   splitting keeps boot parse cost to what boot actually renders. Named
   exports → lazy() via the .then(m => ({default})) adapter. Chunks are
   cached after first open, so a tab visit costs one fetch, once. */
const Contacts = lazyScreen(() => import('./screens/Contacts').then((m) => ({ default: m.Contacts })));
const Content = lazyScreen(() => import('./screens/Content').then((m) => ({ default: m.Content })));
const Releases = lazyScreen(() => import('./screens/Releases').then((m) => ({ default: m.Releases })));
const ReleasePage = lazyScreen(() => import('./screens/ReleasePage').then((m) => ({ default: m.ReleasePage })));
const MarketingGate = lazyScreen(() => import('./screens/MarketingGate').then((m) => ({ default: m.MarketingGate })));
const Assets = lazyScreen(() => import('./screens/Assets').then((m) => ({ default: m.Assets })));
const Buses = lazyScreen(() => import('./screens/Buses').then((m) => ({ default: m.Buses })));
const Antenna = lazyScreen(() => import('./screens/Antenna').then((m) => ({ default: m.Antenna }))); // antenna 2026-07-06 (renamed from Fan CNTRL 2026-07-08)
const Schedule = lazyScreen(() => import('./screens/Schedule').then((m) => ({ default: m.Schedule })));
const Settings = lazyScreen(() => import('./screens/Settings').then((m) => ({ default: m.Settings })));
const Roster = lazyScreen(() => import('./screens/Roster').then((m) => ({ default: m.Roster })));
/* self-music 2026-07-17 (Hopeful via Ryan): the band-surface Music screen,
   the SAME MusicTabBody the roster renders, self-scoped under the band TopNav. */
const Music = lazyScreen(() => import('./screens/Music').then((m) => ({ default: m.Music })));

// Roster-JEDI (owns the roster surface per the JEDI surface split). Kept in sync
// with route-gate.js ROSTER_JEDI_EMAIL and lib/viewAs.
const ROSTER_JEDI_EMAIL = 'cryanward@icloud.com';

// Full-URL roster paths (window.location.pathname includes the /app basename).
const ROSTER_FULLPATH_RE = /^\/app\/(roster|manager|label|booking-dashboard|music)(\/|$)/;

/* staging-badge 2026-07-06: the ac:api:base override redirects API calls to a
   different worker (staging) with NO visual difference, same URL, same UI.
   That's how test data ends up in prod and real work ends up in staging. This
   badge makes the mode unmissable whenever the override is set, on the login
   screen AND in-app. Click = drop the override and reload back to prod.
   Renders nothing for everyone who never set the key. */
function StagingBadge(): JSX.Element | null {
  let base = '';
  try { base = localStorage.getItem('ac:api:base') || ''; } catch { /* ignore */ }
  if (!base) return null;
  const host = base.replace(/^https?:\/\//, '').split('/')[0];
  return (
    <button
      type="button"
      title={'API override active: ' + base + ', click to return to production'}
      onClick={() => {
        if (!window.confirm('Leave staging and reconnect to PRODUCTION?')) return;
        try { localStorage.removeItem('ac:api:base'); } catch { /* ignore */ }
        window.location.reload();
      }}
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        zIndex: 99999,
        background: '#b45309',
        color: '#fff',
        border: 'none',
        borderRadius: 999,
        padding: '6px 14px',
        font: '700 12px/1.2 ui-monospace, monospace',
        letterSpacing: '0.06em',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
      }}
    >
      ⚠ STAGING · {host}
    </button>
  );
}

/* acp 2026-07-24 — Artist CNTRL Pro renders in THIS app on THIS domain,
   the same way roster does: no redirect, no second bundle. The switch is
   isPro(user) from lib/plan.ts (plan_tier === 'acp', billing-flipped).
   Only Booking, Venue assets, and Contacts get Pro variants — gate each
   at its section mount (one check per section), everything else shared. */

/** Org accounts (label / management / booking) + the roster-JEDI belong on the
 *  roster surface, not the artist Dashboard. */
function belongsOnRoster(u: User | null | undefined): boolean {
  if (!u) return false;
  const root = String(u.account_root || '').toLowerCase();
  const email = String(u.email || '').toLowerCase();
  return (root !== '' && root !== 'artist') || email === ROSTER_JEDI_EMAIL;
}

/* oauth-error-surface 2026-07-18, a failed connector return (e.g. Shopify's
   token exchange dying on a bad client secret) used to strip its error param
   and reload with zero feedback. finalizeOAuthReturn stashes the error; this
   renders it once as a dismissible banner. Brand crimson per Bible Ch.04. */
function OAuthErrorBanner({ err, onDismiss }: { readonly err: { provider: string; code: string }; readonly onDismiss: () => void }): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)',
        zIndex: 9999, display: 'flex', alignItems: 'center', gap: 12,
        background: '#8B1A2B', color: '#fff', padding: '12px 16px',
        borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
        fontSize: 13, maxWidth: 'min(92vw, 560px)',
      }}
    >
      <span>
        {err.provider} didn&rsquo;t connect ({err.code.replace(/_/g, ' ')}). Try again from Settings &rarr; Connections, if it keeps failing, the worker log has the reason (wrangler tail cntrl-auth).
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
}

export function App() {
  // Auth gate: no token → the sign-in screen (the host page has none). A
  // successful login sets the token and flips this in-place (no reload), so the
  // routed app mounts authenticated. Mirrors the mobile MobileRoot boot gate.
  const [authed, setAuthed] = useState<boolean>(() => isAuthenticated());

  // Resolved signed-in user + a "booted" flag. We must know the account_root
  // BEFORE painting any routed screen, otherwise an org account briefly FLASHES
  // the artist Dashboard before the roster redirect (reported on label login).
  // Seed synchronously from cache when present (no flash on warm loads/refresh);
  // otherwise hold render until /api/me resolves.
  const [user, setUser] = useState<User | null>(() => getCachedUser() ?? null);
  const [booted, setBooted] = useState<boolean>(() => !isAuthenticated() || !!getCachedUser());

  // OAuth return handler. Any connector that redirects back to /app?<x>_connected=1
  // is finalized here: Meta needs a client-side exchange POST (token never in the
  // URL, the flip broke this on legacy→React), others save server-side. finalize
  // strips the params + busts the connections cache; it returns true when we should
  // reload so the just-connected platform shows WITHOUT a manual refresh.
  const [oauthErr, setOauthErr] = useState<{ provider: string; code: string } | null>(null);
  useEffect(() => {
    void finalizeOAuthReturn().then((shouldReload) => {
      if (shouldReload) { window.location.reload(); return; }
      /* oauth-error-surface 2026-07-18: error returns don't reload, pick up
         the stashed error (if any) and show the banner. */
      setOauthErr(takeOAuthReturnError());
    });
  }, []);

  // shopify-claim 2026-07-13: a COLD App-Store install lands signed-out, so
  // finalizeOAuthReturn stashes the exchange code instead of posting it. The
  // moment a session exists (fresh sign-in or signup after install), claim it,
  // this is what attaches the merchant's store and stamps billing_source.
  // No-op unless the stash key exists; reload so the dashboard + billing gate
  // reflect the new connection immediately.
  useEffect(() => {
    if (!authed) return;
    void claimPendingShopifyExchange().then((claimed) => {
      if (claimed) window.location.reload();
    });
  }, [authed]);

  /* mask-floor 2026-07-15 (live deep-link test find): the boot mask used to
     hide ONLY when a dataCache-backed screen settled its first fetch
     (lib/dataCache.ts hideBootLoader). Screens that fetch outside dataCache
     (e.g. /app/antenna) never fired it, so a DEEP LINK to them sat behind
     the mask until the host page's 12s hard-kill, with the fully loaded
     screen underneath. The mask's contract is "the app has something real
     to show": once the routed screen commits its first frame, every screen
     owns its own loading states. Double-rAF fires after first paint; the
     dataCache path still hides earlier when it settles first. */
  useEffect(() => {
    if (!authed || !booted) return;
    /* Plain timer, NOT requestAnimationFrame, rAF pauses entirely in
       background tabs (found live: a deep link opened in a backgrounded
       window rendered fully but the mask sat until the 12s hard-kill
       because the rAF hide never fired). 250ms after boot ≈ first commit
       with margin; timers run (throttled, still fine) when hidden. */
    /* flash-fix 2026-07-24 (Ryan: "what is the point of the loading X if every
       page flashes the empty screen"): 250ms surrendered the mask before ANY
       data existed. Screens are now cache-backed with skeletons, so the mask's
       primary hide is dataCache's hideBootLoader (first real data). This timer
       is only the safety floor for screens outside dataCache (deep links),
       2.5s beats the old 12s hard-kill without lying at 250ms. */
    const t = setTimeout(() => {
      const w = window as unknown as { acHideLoader?: () => void };
      if (typeof w.acHideLoader === 'function') w.acHideLoader();
    }, 2500);
    return () => clearTimeout(t);
  }, [authed, booted]);

  // Resolve the user once (drives the landing decision + spouse routing). Spouse
  // has NO desktop surface (Bible Ch.05 Part 10, mobile-only) → bounce to the
  // mobile chromeless surface. Everyone else: store the user + mark booted so the
  // routed app can paint on the correct surface.
  useEffect(() => {
    if (!authed) return;
    let live = true;
    const apply = (u: User | null | undefined): void => {
      if (!live) return;
      if (u && String(u.role || '').toLowerCase() === 'spouse') {
        window.location.href = '/m-react/spouse';
        return;
      }
      setUser(u ?? null);
      setBooted(true);
    };
    const cached = getCachedUser();
    if (cached) { apply(cached); return; }
    void fetchCurrentUser().then(apply).catch(() => { if (live) setBooted(true); });
    return () => { live = false; };
  }, [authed]);

  if (!authed) {
    return (
      <>
        <StagingBadge />
        {oauthErr && <OAuthErrorBanner err={oauthErr} onDismiss={() => setOauthErr(null)} />}
        <DesktopLogin
          onAuthed={() => {
            // Seed from cache if warm; otherwise hold render (booted=false) until
            // the boot effect resolves /api/me, so an org login lands straight on
            // the roster with no artist-Dashboard flash.
            const c = getCachedUser() ?? null;
            setUser(c);
            setBooted(!!c);
            setAuthed(true);
          }}
        />
      </>
    );
  }

  // Hold render until we know who this is, prevents the wrong-surface flash.
  if (!booted) return null;

  // Org accounts + roster-JEDI belong on the roster surface. Rewrite the URL
  // BEFORE BrowserRouter reads it so the router mounts straight onto /app/roster
  // (no artist-Dashboard paint). Skip embed iframes: roster loads its artist-
  // scoped tabs as /app/<tab>?embed=1 and must not be bounced.
  if (
    belongsOnRoster(user) &&
    !new URLSearchParams(window.location.search).has('embed') &&
    !ROSTER_FULLPATH_RE.test(window.location.pathname)
  ) {
    window.history.replaceState(null, '', '/app/roster' + window.location.search + window.location.hash);
  }

  // roster-gate 2026-07-03 (SECURITY): RENDER-TIME gate for the roster surface.
  // belongsOnRoster() only drove the redirect ONTO /app/roster, nothing stopped
  // an Artist-root seat (owner / band_member* / TM / crew) from navigating to
  // /app/roster directly and mounting the full shell + its data hooks. Only org
  // roots (label / management / booking) and the roster JEDI may mount it. Fails
  // CLOSED: render is already held on `booted` while the role loads, and an
  // unresolved user (null) → belongsOnRoster false → redirect before <Roster>
  // mounts, so no roster child ever renders or fetches for a non-org seat.
  const rosterGate = (el: JSX.Element): JSX.Element =>
    belongsOnRoster(user) ? el : <Navigate to="/" replace />;

  /* artist-gate 2026-07-24 (Ryan: stripping /roster off the URL let a label
     account mount the artist surface — SECURITY). The boot-time replaceState
     above only steers the INITIAL mount; SPA-internal navigations (catch-all
     redirects, back button) re-render without re-reading it. Render-time gate,
     fails closed, mirror of rosterGate. Embed iframes are exempt: roster
     loads artist-scoped tabs as /app/<tab>?embed=1 under the org session and
     must render the artist body. */
  const artistGate = (el: JSX.Element): JSX.Element =>
    belongsOnRoster(user) && !new URLSearchParams(window.location.search).has('embed')
      ? <Navigate to="/roster" replace />
      : el;

  return (
    // The host page is served at /app, basename keeps client routes
    // (e.g. /app, /app/contacts) resolving correctly. (Legacy was /app-react;
    // _worker.js 301s /app-react → /app for any in-flight links.)
    <><StagingBadge />
    {oauthErr && <OAuthErrorBanner err={oauthErr} onDismiss={() => setOauthErr(null)} />}
    <BrowserRouter basename="/app">
      {/* code-split 2026-07-14: null fallback, each screen paints its own
          TopNav within ~1 chunk fetch; a spinner here would flash for ~50ms
          on first tab open and never again (chunks cache). */}
      <ScreenErrorBoundary>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={artistGate(<Dashboard />)} />
        <Route path="/contacts" element={artistGate(<Contacts />)} />
        {/* nav-ia 2026-07-27 (Ryan) — flat URL renames for the grouped nav:
            /content→/socials, /music→/streaming, Assets split into /assets
            (photos/logos/bio/EPK) + /venue-assets (production stack), /buses
            Pro placeholder. Old paths stay as redirects: bookmarks, push
            routes and stale app builds must keep landing somewhere real. */}
        <Route path="/socials" element={artistGate(<Content />)} />
        <Route path="/content" element={<Navigate to="/socials" replace />} />
        <Route path="/releases" element={artistGate(<Releases />)} />
        <Route path="/releases/:slug" element={artistGate(<ReleasePage />)} />
        <Route path="/marketing" element={artistGate(<MarketingGate />)} />
        <Route path="/assets" element={artistGate(<Assets view="brand" />)} />
        <Route path="/venue-assets" element={artistGate(<Assets view="production" />)} />
        <Route path="/buses" element={artistGate(<Buses />)} />
        <Route path="/antenna" element={artistGate(<Antenna />)} />
        {/* Schedule is role-gated: Booking (money) for booking-tier seats, Calendar
            (read-only) for everyone else. All three paths gate through <Schedule>
            so a direct URL can't bypass the role split (a Calendar seat hitting
            /booking still renders Calendar). */}
        <Route path="/calendar" element={artistGate(<Schedule />)} />
        <Route path="/booking" element={artistGate(<Schedule />)} />
        <Route path="/schedule" element={artistGate(<Schedule />)} />
        <Route path="/settings" element={artistGate(<Settings />)} />
        {/* ── Roster surface ──────────────────────────────────────────────
            /roster is the ONE canonical roster surface. It owns the subview
            (label / management / booking), artist selection, and the active
            tab internally, and is owner-gated (only the roster JEDI sees the
            subview chip). Data is stubbed (structural port).

            The six legacy dev routes below + /music are kept as DEV ALIASES
            that render the SAME Roster surface with the subview (and selection)
            forced, matching the "separate dev tabs for when they re-merge"
            convention. They do not persist state. */}
        <Route path="/roster" element={rosterGate(<Roster />)} />
        <Route path="/manager" element={rosterGate(<Roster forcedSubview="management" forcedSelection="all" />)} />
        <Route path="/manager/artist" element={rosterGate(<Roster forcedSubview="management" forcedSelection="artist" />)} />
        <Route path="/label" element={rosterGate(<Roster forcedSubview="label" forcedSelection="all" />)} />
        <Route path="/label/artist" element={rosterGate(<Roster forcedSubview="label" forcedSelection="artist" />)} />
        {/* NOTE: /booking is the artist Booking screen (above), the booking
            org/roster views live under /booking-dashboard so they do not
            clobber it. */}
        <Route path="/booking-dashboard" element={rosterGate(<Roster forcedSubview="booking" forcedSelection="all" />)} />
        <Route path="/booking-dashboard/artist" element={rosterGate(<Roster forcedSubview="booking" forcedSelection="artist" />)} />
        {/* Music tab. Roster/org seats get the roster shell's artist-scoped
            Music surface; every band seat gets the SAME body self-scoped under
            the band TopNav (self-music 2026-07-17, Hopeful via Ryan, "for
            everyone"). */}
        <Route
          path="/music"
          element={
            belongsOnRoster(user)
              ? rosterGate(<Roster forcedSubview="label" forcedTab="music" forcedSelection="artist" />)
              : <Navigate to="/streaming" replace />
          }
        />
        {/* nav-ia 2026-07-27 — Streaming IS the former band-seat Music page. */}
        <Route path="/streaming" element={artistGate(<Music />)} />
        {/* Unknown paths fall back to the Dashboard for now. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ScreenErrorBoundary>
    </BrowserRouter></>
  );
}
